import { useEffect, useMemo, useState } from "react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Copy, RefreshCw, Check, ExternalLink, Eye, Clock, Info } from "lucide-react";

function _relativeAgo(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return "hace segundos";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    const days = Math.floor(diff / 86400);
    return days === 1 ? "hace 1 día" : `hace ${days} días`;
  } catch { return null; }
}

export function ShareOrderDialog({ open, onOpenChange, serviceOrder, onChanged }) {
  const { hasPermission, session } = useAuth();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [stats, setStats] = useState(null);
  const [expiresDate, setExpiresDate] = useState("");
  const [savingExpiry, setSavingExpiry] = useState(false);

  // Fase 7 gating: STRICT when the tenant has already migrated to client_portal
  // (i.e. the user has AT LEAST ONE client_portal:* permission). Otherwise use
  // the backward-compat fallback to service_orders:*.
  const hasAnyClientPortalPerm = useMemo(() => {
    const perms = session?.permissions || [];
    return perms.some((p) => p.startsWith("client_portal:"));
  }, [session]);

  function hasClientPortal(action, fallback) {
    if (session?.user?.is_super_admin) return true;
    const strict = hasPermission(`client_portal:${action}`);
    if (hasAnyClientPortalPerm) return strict;
    return strict || hasPermission(fallback);
  }

  const canViewLink = hasClientPortal("view_link", "service_orders:view");
  const canManageContent = hasClientPortal("manage_public_content", "service_orders:edit");
  const canRevoke = hasClientPortal("revoke_link", "service_orders:edit");
  const canGenerate = hasClientPortal("generate_link", "service_orders:edit");

  const publicUrl = useMemo(() => {
    if (!serviceOrder?.public_token) return null;
    const origin = window.location.origin;
    return `${origin}/portal/o/${serviceOrder.public_token}`;
  }, [serviceOrder?.public_token]);

  const enabled = serviceOrder?.public_share_enabled ?? true;

  useEffect(() => {
    if (!open || !serviceOrder?.id) return;
    let alive = true;
    api.get(`/service-orders/${serviceOrder.id}/portal-stats`)
      .then(({ data }) => {
        if (!alive) return;
        setStats(data);
        setExpiresDate(data.public_expires_at ? data.public_expires_at.slice(0, 10) : "");
      })
      .catch(() => { if (alive) setStats(null); });
    return () => { alive = false; };
  }, [open, serviceOrder?.id, serviceOrder?.public_token, serviceOrder?.public_share_enabled]);

  async function copyLink() {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  }

  async function toggleShare() {
    setBusy(true);
    try {
      await api.post(`/service-orders/${serviceOrder.id}/toggle-share`);
      toast.success(enabled ? "Enlace público desactivado" : "Enlace público activado");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function rotateToken() {
    setBusy(true);
    try {
      await api.post(`/service-orders/${serviceOrder.id}/regenerate-token`);
      toast.success("Enlace rotado. El anterior queda invalidado.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      setConfirmRotate(false);
    }
  }

  async function saveExpiration(clear = false) {
    setSavingExpiry(true);
    try {
      let payload = { expires_at: null };
      if (!clear && expiresDate) {
        // Convert YYYY-MM-DD to end-of-day UTC to be lenient
        payload = { expires_at: `${expiresDate}T23:59:59Z` };
      }
      await api.post(`/service-orders/${serviceOrder.id}/portal-expiration`, payload);
      toast.success(clear ? "Expiración eliminada" : "Expiración actualizada");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingExpiry(false);
    }
  }

  function openPreview() {
    if (!publicUrl) return;
    window.open(publicUrl, "_blank", "noopener,noreferrer");
  }

  const visits = stats?.public_access_count ?? 0;
  const lastAgo = _relativeAgo(stats?.public_last_accessed_at);
  const isExpired = !!stats?.is_expired;
  const isRevoked = !!stats?.is_revoked;
  const isActive = !!stats?.is_active;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="dialog-share-order">
          <DialogHeader>
            <DialogTitle>Portal del cliente</DialogTitle>
            <DialogDescription>
              Enlace público de solo lectura para el folio{" "}
              <span className="mono font-medium">{serviceOrder?.folio}</span>. El cliente verá
              estatus, historial y evidencias marcadas como visibles. Sin login, sin edición.
            </DialogDescription>
          </DialogHeader>

          {/* Estado del portal */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="share-status-badges">
            {isActive && <Badge className="bg-emerald-100 text-emerald-700">Activo</Badge>}
            {isRevoked && <Badge className="bg-red-100 text-red-700">Desactivado</Badge>}
            {isExpired && <Badge className="bg-amber-100 text-amber-700">Expirado</Badge>}
            {!stats?.has_token && <Badge className="bg-slate-100 text-slate-700">Sin enlace generado</Badge>}
          </div>

          <div className="space-y-5">
            {enabled && publicUrl && canViewLink ? (
              <>
                <div className="flex items-center justify-center py-4 bg-white border border-border rounded-lg">
                  <div className="bg-white p-3 rounded-md" data-testid="share-qr">
                    <QRCode value={publicUrl} size={180} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Enlace</Label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={publicUrl}
                      className="mono text-xs"
                      data-testid="share-url"
                      onFocus={(e) => e.target.select()}
                    />
                    <Button variant="outline" size="icon" onClick={copyLink} data-testid="share-copy">
                      {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="icon" onClick={openPreview} data-testid="share-preview" title="Vista previa como cliente">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    También aparece impreso como QR en el PDF de orden de ingreso.
                  </p>
                </div>

                {/* Métricas de uso — Fase 7 */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-md border border-border" data-testid="share-visits">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Visitas</div>
                    <div className="text-lg font-semibold mono">{visits}</div>
                  </div>
                  <div className="p-3 rounded-md border border-border" data-testid="share-last-visit">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Última visita</div>
                    <div className="text-sm">{lastAgo || <span className="text-muted-foreground">Sin visitas aún</span>}</div>
                  </div>
                </div>

                {/* Expiración — Fase 7 */}
                {canManageContent && (
                  <div className="p-3 rounded-md border border-border space-y-2" data-testid="share-expiration">
                    <Label className="flex items-center gap-2 text-sm">
                      <Clock className="h-4 w-4" /> Expiración del portal
                    </Label>
                    <div className="flex flex-wrap gap-2 items-center">
                      <Input
                        type="date"
                        value={expiresDate}
                        onChange={(e) => setExpiresDate(e.target.value)}
                        data-testid="share-expiration-date"
                        className="max-w-[190px]"
                      />
                      <Button size="sm" onClick={() => saveExpiration(false)} disabled={savingExpiry || !expiresDate} data-testid="share-expiration-save">
                        Guardar
                      </Button>
                      {stats?.public_expires_at && (
                        <Button size="sm" variant="outline" onClick={() => saveExpiration(true)} disabled={savingExpiry} data-testid="share-expiration-clear">
                          Quitar
                        </Button>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Al entregar la orden se aplica automáticamente <strong>+{stats?.portal_auto_expiry_days_on_delivery ?? 5} días</strong>
                      {" "}si no hay expiración manual configurada. Fechas pasadas no permitidas.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-border p-6 text-center bg-slate-50">
                <p className="text-sm text-slate-700 font-medium mb-1">
                  {!stats?.has_token ? "Sin enlace generado" : "Enlace público desactivado"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {!stats?.has_token
                    ? "El enlace se crea automáticamente al generar la orden."
                    : "Ningún cliente puede acceder al portal de esta orden en este momento."}
                </p>
              </div>
            )}

            {canRevoke && (
              <div className="flex items-center justify-between p-3 rounded-md border border-border">
                <div>
                  <Label className="text-sm">Compartir enlace público</Label>
                  <p className="text-xs text-muted-foreground">
                    Al desactivar, el cliente ve un mensaje genérico de &quot;no encontrado&quot;.
                  </p>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={toggleShare}
                  disabled={busy}
                  data-testid="share-toggle"
                />
              </div>
            )}

            {!canViewLink && (
              <Alert data-testid="share-no-perm-alert">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Tu rol no tiene permiso para ver el enlace del portal. Contacta al administrador.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {canGenerate && (
              <Button
                variant="outline"
                onClick={() => setConfirmRotate(true)}
                disabled={busy || !enabled}
                data-testid="share-rotate"
                className="sm:mr-auto"
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Rotar enlace
              </Button>
            )}
            <Button onClick={() => onOpenChange(false)} className="btn-tenant" data-testid="share-close">
              Listo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotar enlace público</AlertDialogTitle>
            <AlertDialogDescription>
              El enlace y QR anteriores dejarán de funcionar. Cualquier persona que tenga el
              enlace viejo verá &quot;no encontrado&quot;. El contador de visitas se reinicia. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={rotateToken}
              className="bg-red-600 hover:bg-red-700"
              data-testid="share-rotate-confirm"
            >
              Rotar enlace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
